import { useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { SearchX, ChevronLeft, ChevronRight } from 'lucide-react';
import { TID, SORT_OPTIONS, PLATFORM_MAX_GUESTS, type SearchFilters, type SortOption } from '@yalidavet/shared';
import HeroSearch from '@/components/search/HeroSearch';
import QuickFilters from '@/components/search/QuickFilters';
import FiltersPanel, { defaultFilterState, filterStateToParams, type FilterState } from '@/components/search/FiltersPanel';
import MansionCard from '@/components/mansion/MansionCard';
import { api } from '@/lib/api';

export default function SearchResultsPage() {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(() => hydrateFilters(params));

  const query: Partial<SearchFilters> = useMemo(() => {
    const q: Partial<SearchFilters> = {
      district: params.get('district') ?? undefined,
      city: params.get('city') ?? undefined,
      propertyType: (params.get('propertyType') as SearchFilters['propertyType']) ?? undefined,
      checkin: params.get('checkin') ?? undefined,
      checkout: params.get('checkout') ?? undefined,
      guestCount: params.get('guestCount') ? Number(params.get('guestCount')) : undefined,
      eventTypeId: params.get('eventTypeId') ?? undefined,
      sort: (params.get('sort') as SortOption) ?? 'price_asc',
      page: Number(params.get('page') ?? 1),
      pageSize: 12,
      ...filterStateToParams(filters),
    };
    return q;
  }, [params, filters]);

  // The platform hard-caps events at 150 guests. Guard client-side so the user
  // gets an explicit explanation instead of an opaque 400 from the API.
  const requestedGuests = params.get('guestCount') ? Number(params.get('guestCount')) : undefined;
  const overPlatformLimit =
    requestedGuests !== undefined && Number.isFinite(requestedGuests) && requestedGuests > PLATFORM_MAX_GUESTS;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['search', query],
    queryFn: () => api.searchMansions(query),
    placeholderData: keepPreviousData,
    enabled: !overPlatformLimit,
  });

  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v === null || v === '' ? next.delete(k) : next.set(k, v)));
    if (!('page' in patch)) next.set('page', '1');
    setParams(next, { replace: false });
  };

  const searchQueryString = params.toString();

  return (
    <>
      {/* ---------- Sticky search retaining active parameters ---------- */}
      <div
        className="sticky top-16 z-40 border-b border-ink-100 bg-white/95 py-3 backdrop-blur"
        data-testid={TID.stickySearch}
      >
        <div className="yd-container space-y-3">
          <HeroSearch
            compact={false}
            submitLabel="Güncelle"
            submitTestId={TID.stickySearchUpdate}
            testId="sticky-search-form"
            initial={{
              district: params.get('district') ?? '',
              checkin: params.get('checkin') ?? '',
              checkout: params.get('checkout') ?? '',
              propertyType: params.get('propertyType') ?? '',
              guestCount: params.get('guestCount') ? Number(params.get('guestCount')) : undefined,
            }}
            onSubmit={(v) =>
              patchParams({
                district: v.district ?? null,
                checkin: v.checkin ?? null,
                checkout: v.checkout ?? null,
                propertyType: v.propertyType ?? null,
                guestCount: v.guestCount ? String(v.guestCount) : null,
              })
            }
          />
          <QuickFilters
            active={params.get('eventTypeId')}
            onSelect={(id) => patchParams({ eventTypeId: id })}
          />
        </div>
      </div>

      <div className="yd-container grid gap-8 py-8 lg:grid-cols-[280px_1fr]">
        {/* ---------- Sidebar ---------- */}
        <FiltersPanel
          value={filters}
          guestCount={query.guestCount}
          onApply={(next) => {
            setFilters(next);
            patchParams({ page: '1', ...serializeFilters(next) });
          }}
          onReset={() => {
            const fresh = defaultFilterState();
            setFilters(fresh);
            patchParams({
              minPriceKurus: null, maxPriceKurus: null, minAreaM2: null, maxAreaM2: null,
              hasPool: null, hasGarden: null, hasSeaView: null, hasParking: null,
            });
          }}
        />

        {/* ---------- Results ---------- */}
        <section>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium text-ink-800" data-testid={TID.resultsCount} data-total={overPlatformLimit ? 0 : data?.total ?? 0}>
              {overPlatformLimit
                ? 'Aramanıza uygun mülk bulunamadı'
                : isLoading
                  ? 'Mülkler aranıyor…'
                  : data?.summaryTr}
            </p>

            <label className="flex items-center gap-2 text-sm">
              <span className="text-ink-500">Sırala:</span>
              <select
                className="yd-input w-auto py-2"
                value={params.get('sort') ?? 'price_asc'}
                onChange={(e) => patchParams({ sort: e.target.value })}
                data-testid={TID.sortSelect}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Applied tier badge — proves the garden math to the user */}
          {data?.appliedTier && (
            <p
              className="mb-4 rounded-lg bg-ink-50 px-3 py-2 text-xs text-ink-600"
              data-testid="results-tier-badge"
              data-tier={data.appliedTier}
              data-required-garden={data.requiredGardenM2}
            >
              Tier {data.appliedTier} eşleştirmesi uygulandı · minimum {data.requiredGardenM2} m² bahçe ·
              kapasitesi {query.guestCount} kişiden düşük mülkler gizlendi.
            </p>
          )}

          {overPlatformLimit ? (
            <div className="yd-card flex flex-col items-center gap-3 p-12 text-center" data-testid={TID.emptyState}>
              <SearchX className="h-10 w-10 text-ink-300" aria-hidden />
              <h2 className="font-semibold text-ink-900">Misafir sayısı platform limitini aşıyor</h2>
              <p className="max-w-md text-sm text-ink-500">
                Yalıda Davet üzerinden en fazla {PLATFORM_MAX_GUESTS} misafirlik organizasyon
                planlayabilirsiniz. {requestedGuests} misafir için lütfen misafir sayısını
                {' '}{PLATFORM_MAX_GUESTS} veya altına düşürün.
              </p>
              <Link to="/ara" className="yd-btn-secondary">Filtreleri temizle</Link>
            </div>
          ) : isLoading ? (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="yd-skeleton h-80" />)}
            </div>
          ) : data && data.items.length > 0 ? (
            <>
              <div
                className={`grid gap-5 sm:grid-cols-2 xl:grid-cols-3 ${isFetching ? 'opacity-60' : ''}`}
                data-testid={TID.resultsGrid}
                data-count={data.items.length}
              >
                {data.items.map((m) => (
                  <MansionCard key={m.id} mansion={m} searchQuery={searchQueryString} />
                ))}
              </div>

              {data.totalPages > 1 && (
                <nav className="mt-8 flex items-center justify-center gap-2" aria-label="Sayfalama">
                  <button
                    type="button"
                    className="yd-btn-secondary"
                    disabled={data.page <= 1}
                    onClick={() => patchParams({ page: String(data.page - 1) })}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="h-4 w-4" /> Önceki
                  </button>
                  <span className="px-3 text-sm text-ink-600" data-testid="pagination-status">
                    {data.page} / {data.totalPages}
                  </span>
                  <button
                    type="button"
                    className="yd-btn-secondary"
                    disabled={data.page >= data.totalPages}
                    onClick={() => patchParams({ page: String(data.page + 1) })}
                    data-testid="pagination-next"
                  >
                    Sonraki <ChevronRight className="h-4 w-4" />
                  </button>
                </nav>
              )}
            </>
          ) : (
            <div className="yd-card flex flex-col items-center gap-3 p-12 text-center" data-testid={TID.emptyState}>
              <SearchX className="h-10 w-10 text-ink-300" aria-hidden />
              <h2 className="font-semibold text-ink-900">Aramanıza uygun mülk bulunamadı</h2>
              <p className="max-w-md text-sm text-ink-500">
                {query.guestCount
                  ? `${query.guestCount} misafir için gereken bahçe alanını karşılayan mülk yok. Misafir sayısını düşürmeyi veya filtreleri genişletmeyi deneyin.`
                  : 'Filtrelerinizi genişletmeyi veya farklı bir lokasyon seçmeyi deneyin.'}
              </p>
              <Link to="/ara" className="yd-btn-secondary">Filtreleri temizle</Link>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function hydrateFilters(params: URLSearchParams): FilterState {
  const base = defaultFilterState();
  const num = (k: string, fallback: number) => (params.get(k) ? Number(params.get(k)) : fallback);
  return {
    minPriceKurus: num('minPriceKurus', base.minPriceKurus),
    maxPriceKurus: num('maxPriceKurus', base.maxPriceKurus),
    minAreaM2: num('minAreaM2', base.minAreaM2),
    maxAreaM2: num('maxAreaM2', base.maxAreaM2),
    hasPool: params.get('hasPool') === 'true',
    hasGarden: params.get('hasGarden') === 'true',
    hasSeaView: params.get('hasSeaView') === 'true',
    hasParking: params.get('hasParking') === 'true',
  };
}

function serializeFilters(f: FilterState): Record<string, string | null> {
  const p = filterStateToParams(f);
  return {
    minPriceKurus: p.minPriceKurus != null ? String(p.minPriceKurus) : null,
    maxPriceKurus: p.maxPriceKurus != null ? String(p.maxPriceKurus) : null,
    minAreaM2: p.minAreaM2 != null ? String(p.minAreaM2) : null,
    maxAreaM2: p.maxAreaM2 != null ? String(p.maxAreaM2) : null,
    hasPool: p.hasPool ? 'true' : null,
    hasGarden: p.hasGarden ? 'true' : null,
    hasSeaView: p.hasSeaView ? 'true' : null,
    hasParking: p.hasParking ? 'true' : null,
  };
}
